import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { UserRole } from '../types/user';

interface ProtectedRouteProps {
  requiredRole?: UserRole;
  requireAdmin?: boolean; // Set this to true if only admins can access
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRole, requireAdmin = false }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // Display a loading state while we check the user's authentication status
  if (isLoading) {
    return <div>Loading...</div>; // You can replace this with your Loader component
  }
  
  // If not authenticated, redirect to the login page
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user is authenticated, but user object is null (shouldn't happen with isAuthenticated check, but defensive)
  if (!user) {
    return <Navigate to="/unauthorized" replace />;
  }

  // ✅ NEW HIERARCHICAL LOGIC: Admins satisfy all role requirements
  if (requireAdmin) {
    if (user.isAdmin) {
      return <Outlet />; // Admin, access granted
    } else {
      return <Navigate to="/unauthorized" replace />; // Not admin, access denied
    }
  }

  // If a specific role is required (and not an admin route)
  if (requiredRole) {
    // If the user is an admin, they can access any role-restricted page
    if (user.isAdmin) { // ✅ Admin bypasses specific role check
      return <Outlet />;
    }
    
    // Otherwise, check if their role matches the required role
    if (user.role === requiredRole) {
      return <Outlet />;
    } else {
      return <Navigate to="/unauthorized" replace />; // Role doesn't match, access denied
    }
  }

  // If no specific role or admin is required, just authentication is enough
  return <Outlet />;
};

export default ProtectedRoute;